import { useMemo } from 'react';
import { remark } from 'remark';
import remarkGfm from 'remark-gfm';
import remarkHtml from 'remark-html';
import DOMPurify from 'dompurify';

import styles from './MarkdownContainer.module.css';

/**
 * @param {string} line
 * @returns {boolean}
 */
function isMarkdownTableLine(line) {
  return /^\s*\|/.test(line);
}

/**
 * Normalize line breaks for mixed `<br>` / `\r\n` / `\n` content while preserving GFM table rows.
 * @param {string} content
 * @returns {string}
 */
function normalizeMarkdownContent(content) {
  const withNewlines = content
    .replace(/\r\n?/g, '\n')
    .replace(/<br\s*\/?>[^\S\n]*\n?/gi, '\n');

  return withNewlines
    .split('\n')
    .map((line) => (isMarkdownTableLine(line) ? line : `${line}  `))
    .join('\n');
}

/**
 * MarkdownContainer component - Simple markdown to HTML renderer
 * @param {object} props
 * @param {string} [props.content] - Markdown content to render
 * @param {string} [props.className] - CSS classes for styling
 * @param {string} [props.placeholder=''] - Placeholder for empty content
 * @param {'div'|'span'} [props.as='div'] - HTML tag to render (use 'span' inside button/legend/phrasing content)
 * @returns {JSX.Element}
 */
function MarkdownContainer({
  content, className = '', placeholder = '', as: Tag = 'div',
}) {
  const htmlContent = useMemo(() => {
    if (!content) {
      return placeholder;
    }

    try {
      const normalized = normalizeMarkdownContent(content);

      const processed = remark()
        .use(remarkGfm)
        .use(remarkHtml, { sanitize: false })
        .processSync(normalized);

      return DOMPurify.sanitize(processed.toString(), { USE_PROFILES: { html: true } });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error processing markdown:', error);
      return content; // Show raw markdown if processing fails
    }
  }, [content, placeholder]);

  if (!content) {
    return <span className={className}>{placeholder}</span>;
  }

  return (
    <Tag
      className={`${styles.markdown} ${className}`}
      dangerouslySetInnerHTML={{ __html: htmlContent }}
    />
  );
}

export default MarkdownContainer;
